import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  ToggleButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  donutChartConfigSelector,
  donutChartConfigDispatcher,
} from "@store/slices/donut-chart-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { questionSessionMemory } from "@/utils/question-session-memory";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import ConfigurableDonutChart from "./ConfigurableDonutChart";
import { getChartColor } from "@utils/chart-utils";
import HPButton from "../common/Button";
import SliceGroupComponent from "./SliceGroupComponent";
import HPInput from "../common/Input";
import IOSSwitch from "../common/Switch";
import DrawerFilterToggles from "./common/DrawerFilterToggles";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyChartState from "../common/EmptyChartState";
import { useDebounce } from "@hooks/use-debounce";

const DonutChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    currentConfig,
    datasetId,
    datasetName,
    questionId,
    datasetVersionId,
    activeFilters,
    applyFilters,
  } = useAppSelector(donutChartConfigSelector);
  const { isDrawerOpen, editingWidgetId, pendingMemoryEntry } =
    useAppSelector(chartDrawerSelector);
  const questions = useAppSelector((state) => state.donutChartConfig.question);
  const [weightsApplied, setWeightsApplied] = React.useState<boolean>(true);

  const isCreatingChart = editingWidgetId === "new-widget";

  const [getDatasetQuestionsResponse, { isLoading: isRefetchingData }] =
    useGetDatasetQuestionsResponseMutation();

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateWeight,
    updateDataSorting,
    updateDataLabelsPosition,
    updateLegendEnabled,
    updateColorPalette,
    updatePercentageFormatting,
    updateSubOnePercentStyle,
    updateSliceValueFormat,
    updateMinSliceTextThreshold,
    updateForceShowAllValues,
    updateEmphasis,
    updateEmphasisDisplayFormat,
    updateEmphasisCustomText,
    addGroup,
    removeGroup,
    updateChartData,
    toggleApplyFilters,
    saveCurrentConfig,
    resetToDefault,
  } = donutChartConfigDispatcher(dispatch);

  // Local state for immediate UI feedback
  // Initialize based on whether filters are active
  const [localApplyFilters, setLocalApplyFilters] = useState(
    activeFilters.length > 0 ? true : false,
  );
  const [isTogglingFilters, setIsTogglingFilters] = useState(false);
  const isTogglingFiltersRef = React.useRef(false);

  // Toggle for using dashboard date filters in preview
  const [usePreviewDateFilter, setUsePreviewDateFilter] = useState(false);

  // Debounce the filter toggle to avoid too many API calls
  const debouncedApplyFilters = useDebounce(localApplyFilters, 500);

  // Sync local state with active filters only on mount or when filters are added/removed
  // but NOT when user is actively toggling
  useEffect(() => {
    // Don't sync if user is actively toggling
    if (isTogglingFiltersRef.current) {
      return;
    }
    // If filters become active/inactive, update toggle to match
    if (activeFilters.length > 0) {
      setLocalApplyFilters(true);
    } else {
      setLocalApplyFilters(false);
    }
  }, [activeFilters.length]);

  // Fetch data with current filter settings
  const fetchChartData = useCallback(
    async (
      weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT",
      shouldApplyFilters: boolean,
    ) => {
      if (!datasetId || !questionId) return;

      try {
        const result = await getDatasetQuestionsResponse({
          id: questionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: weight,
          chartType: "DONUT",
          filters:
            shouldApplyFilters && activeFilters.length > 0
              ? activeFilters
              : undefined,
        });

        if (result.data?.data && Array.isArray(result.data.data)) {
          const processedData = result.data.data.map(
            (item: any, index: number) => {
              // Extract value from new descriptiveMetrics structure with fallback to percentage
              let value = 0;
              let valueFound = false;
              const firstSurvey = item.surveys?.[0];

              if (firstSurvey) {
                // Try descriptiveMetrics first (new structure)
                if (
                  firstSurvey.descriptiveMetrics &&
                  firstSurvey.descriptiveMetrics.length > 0
                ) {
                  const metricValue = firstSurvey.descriptiveMetrics[0].value;
                  if (metricValue !== undefined && metricValue !== null) {
                    value = metricValue;
                    valueFound = true;
                  }
                }
                // Fallback to percentage field (old structure) only if no value was found
                if (
                  !valueFound &&
                  firstSurvey.percentage !== undefined &&
                  firstSurvey.percentage !== null
                ) {
                  value = firstSurvey.percentage;
                }
              }

              return {
                name: item.variableLabel,
                y: value,
                color: getChartColor(index),
              };
            },
          );

          updateChartData({
            categories: result.data.data.map((item: any) => item.variableLabel),
            series: [
              {
                name: questions?.name || "Data",
                data: processedData,
              },
            ],
          });

          // Re-apply the current color palette to the new data
          updateColorPalette(currentConfig.colorPalette);

          // Update weightsApplied state from API response
          if (result.data?.weightsApplied !== undefined) {
            setWeightsApplied(result.data.weightsApplied);
          }
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    },
    [
      datasetId,
      questionId,
      questions?.name,
      activeFilters,
      getDatasetQuestionsResponse,
      updateChartData,
      updateColorPalette,
      currentConfig.colorPalette,
    ],
  );

  // Fetch data when drawer opens for editing (not creating)
  // For initial load when editing, use the current filter state from when drawer opened
  useEffect(() => {
    if (isDrawerOpen && !isCreatingChart && datasetId && questionId) {
      // Use current filter state for initial load (matches crosstab behavior)
      const shouldApplyFiltersForInitialLoad =
        activeFilters.length > 0 && localApplyFilters;
      fetchChartData(currentConfig.weight, shouldApplyFiltersForInitialLoad);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isCreatingChart]);

  const handleWeightChange = useCallback(
    async (newWeight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT") => {
      updateWeight(newWeight);

      // Fetch new data with current filter settings
      await fetchChartData(newWeight, debouncedApplyFilters);
    },
    [updateWeight, fetchChartData, debouncedApplyFilters],
  );

  // Handle filter toggle with immediate UI update
  const handleFilterToggle = useCallback(() => {
    // Set the ref IMMEDIATELY (synchronous) before any state changes
    isTogglingFiltersRef.current = true;
    setIsTogglingFilters(true);
    const newValue = !localApplyFilters;
    setLocalApplyFilters(newValue);
  }, [localApplyFilters]);

  const handlePreviewDateFilterToggle = useCallback(
    async (enabled: boolean) => {
      setUsePreviewDateFilter(enabled);
      // Donut charts typically don't have time period configuration
      // But we can still show the toggle for consistency
      // When enabled, this would filter the data by the dashboard date range
      // For now, this is a UI-only feature for donut charts
      if (datasetId && questionId) {
        await fetchChartData(currentConfig.weight, debouncedApplyFilters);
      }
    },
    [
      datasetId,
      questionId,
      currentConfig.weight,
      debouncedApplyFilters,
      fetchChartData,
    ],
  );

  // Effect to refetch data when debounced filter toggle changes
  useEffect(() => {
    // Only run when we're actively toggling filters
    if (!isTogglingFilters) {
      return;
    }

    if (datasetId && questionId) {
      const refetch = async () => {
        await fetchChartData(currentConfig.weight, debouncedApplyFilters);
        // Use setTimeout to ensure state updates don't trigger other effects
        setTimeout(() => {
          isTogglingFiltersRef.current = false;
          setIsTogglingFilters(false);
        }, 0);
      };
      refetch();
    } else {
      isTogglingFiltersRef.current = false;
      setIsTogglingFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApplyFilters]);

  // Get available slice options for emphasis selection
  const getEmphasisOptions = () => {
    const options = [];

    // Add individual visible slice options (actual slice names)
    currentConfig.series[0]?.data.forEach((slice) => {
      options.push({
        value: `slice:${slice.name}`,
        label: slice.name,
        type: "individual",
      });
    });

    // Add group slice options (both visible and hidden groups)
    currentConfig.groups.forEach((group) => {
      if (group.name.trim() && group.sliceNames.length >= 2) {
        options.push({
          value: `group:${group.id}`,
          label: `${group.name} (Group)`,
          type: "group",
        });
      }
    });

    // Add dynamic options
    options.push({
      value: "highest",
      label: "Highest Percentage",
      type: "dynamic",
    });

    // Add None as default
    options.push({
      value: "none",
      label: "None",
      type: "default",
    });

    return options;
  };

  // Get slices that are already used in groups
  const getUsedSlices = () => {
    const used: string[] = [];
    currentConfig.groups.forEach((group) => {
      used.push(...group.sliceNames);
    });
    return used;
  };

  const handleAddGroup = () => {
    addGroup();
  };

  const handleRemoveGroup = (groupId: string) => {
    removeGroup(groupId);
  };

  const handleSave = async () => {
    // Store the current preview state
    const wasShowingFiltered = localApplyFilters;

    // Only fetch unfiltered data if filters were applied
    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      await fetchChartData(currentConfig.weight, false); // false = no filters
    }

    if (pendingMemoryEntry) {
      questionSessionMemory.push(pendingMemoryEntry.dashboardId, {
        questionId: pendingMemoryEntry.questionId,
        questionType: pendingMemoryEntry.questionType,
        datasetId: pendingMemoryEntry.datasetId,
        slot: "general",
      });
    }

    saveCurrentConfig();

    // Close drawer after 50ms to allow widget save
    setTimeout(() => {
      closeChartDrawer();
    }, 50);

    // Re-apply filters after 100ms if they were active
    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      setTimeout(() => {
        fetchChartData(currentConfig.weight, true); // true = apply filters
      }, 100);
    }
  };

  const handleDiscard = () => {
    resetToDefault();
    closeChartDrawer();
  };

  // Validation for slice grouping
  const hasValidationErrors = () => {
    return currentConfig.groups.some((group) => {
      // Groups with slices selected must have at least 2 slices
      if (group.sliceNames.length > 0 && group.sliceNames.length < 2) {
        return true;
      }
      // Groups with 2+ slices must have a name
      if (group.sliceNames.length >= 2 && !group.name.trim()) {
        return true;
      }
      return false;
    });
  };

  const getValidationTooltip = () => {
    const invalidGroups = currentConfig.groups.filter((group) => {
      if (group.sliceNames.length > 0 && group.sliceNames.length < 2) {
        return true;
      }
      if (group.sliceNames.length >= 2 && !group.name.trim()) {
        return true;
      }
      return false;
    });

    if (invalidGroups.length === 0) return "";

    const messages: string[] = [];
    invalidGroups.forEach((group) => {
      if (group.sliceNames.length > 0 && group.sliceNames.length < 2) {
        messages.push("Groups must have at least 2 slices");
      }
      if (group.sliceNames.length >= 2 && !group.name.trim()) {
        messages.push("Groups with slices must have a name");
      }
    });

    return [...new Set(messages)].join(". ");
  };

  const isValidationError = hasValidationErrors();
  const isAvailableData =
    !currentConfig.series[0]?.data || currentConfig.series[0].data.length === 0;

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="flex-1">
            <Typography className="font-bold" variant="h6">
              {questions?.label || "Donut Chart Configuration"}
            </Typography>
            <Typography variant="body1">
              {questions?.name || "Configure your donut chart"}
            </Typography>
            {datasetName && (
              <Typography variant="caption" color="text.secondary">
                Dataset: {datasetName}
              </Typography>
            )}
          </div>
          {(() => {
            const hasDashboardDateFilters = !!(
              new URLSearchParams(window.location.search).get("df_start") &&
              new URLSearchParams(window.location.search).get("df_end")
            );
            return (
              <>
                <DrawerFilterToggles
                  hasDashboardDateFilters={hasDashboardDateFilters}
                  usePreviewDateFilter={usePreviewDateFilter}
                  onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
                  activeFiltersCount={activeFilters.length}
                  localApplyFilters={localApplyFilters}
                  onFilterToggle={handleFilterToggle}
                  disabled={isTogglingFilters || isRefetchingData}
                />
              </>
            );
          })()}
        </Box>
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Configurations Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Configurations
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Weight Selection */}
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Weight Selection
                    </FormLabel>
                    <Select
                      value={currentConfig.weight}
                      onChange={(e) =>
                        handleWeightChange(
                          e.target.value as
                            | "DISABLED"
                            | "DATASET_DEFAULT"
                            | "SURVEY_WEIGHT",
                        )
                      }
                      disabled={isRefetchingData}
                    >
                      <MenuItem value="DISABLED">Disabled</MenuItem>
                      <MenuItem value="DATASET_DEFAULT">
                        Use Dataset Default
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {currentConfig.weight === "DISABLED" && (
                    <Alert
                      severity="warning"
                      icon={<WarningIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        "& .MuiAlert-message": { fontSize: "0.75rem" },
                      }}
                    >
                      Weights disabled: unweighted counts shown.
                    </Alert>
                  )}

                  {currentConfig.weight === "DATASET_DEFAULT" &&
                    !weightsApplied && (
                      <Alert
                        severity="warning"
                        icon={<WarningIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          "& .MuiAlert-message": { fontSize: "0.75rem" },
                        }}
                      >
                        No default weight configured for this dataset. Showing
                        unweighted counts.
                      </Alert>
                    )}

                  {/* Data Sorting */}
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Data Sorting
                    </FormLabel>
                    <Select
                      value={currentConfig.dataSorting}
                      onChange={(e) =>
                        updateDataSorting(
                          e.target.value as "ascending" | "descending" | "none",
                        )
                      }
                    >
                      <MenuItem value="none">Dataset Value Order</MenuItem>
                      <MenuItem value="ascending">Ascending %</MenuItem>
                      <MenuItem value="descending">Descending %</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider className="mx-[-24px]" />

              {/* Data Grouping Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Slice grouping
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    {currentConfig.groups.map((group) => (
                      <SliceGroupComponent
                        key={group.id}
                        group={group}
                        availableSlices={
                          currentConfig.series[0]?.data.map(
                            (slice) => slice.name,
                          ) || []
                        }
                        usedSlices={getUsedSlices().filter(
                          (slice) => !group.sliceNames.includes(slice),
                        )}
                        onRemove={handleRemoveGroup}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <HPButton
                      size="small"
                      variant="outlined"
                      onClick={handleAddGroup}
                    >
                      <Typography variant="body2" color="primary">
                        + Add Group
                      </Typography>
                    </HPButton>
                  </Box>
                </Box>
              </Box>

              <Divider className="mx-[-24px]" />

              {/* Center Value Configuration Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Center Value Configuration
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Emphasis
                    </FormLabel>
                    <Select
                      value={currentConfig.emphasis}
                      onChange={(e) => updateEmphasis(e.target.value)}
                    >
                      {getEmphasisOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {currentConfig.emphasis !== "none" && (
                    <>
                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Emphasis Display Format
                        </FormLabel>
                        <Select
                          value={currentConfig.emphasisDisplayFormat}
                          onChange={(e) =>
                            updateEmphasisDisplayFormat(
                              e.target.value as
                                | "percentage_title"
                                | "percentage_custom",
                            )
                          }
                        >
                          <MenuItem value="percentage_title">
                            Percentage + Title
                          </MenuItem>
                          <MenuItem value="percentage_custom">
                            Percentage + Custom Text
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {currentConfig.emphasisDisplayFormat ===
                        "percentage_custom" && (
                        <HPInput
                          label="Custom Text"
                          value={currentConfig.emphasisCustomText}
                          onChange={(e) =>
                            updateEmphasisCustomText(e.target.value)
                          }
                          placeholder="Enter custom text"
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
              <Divider className="mx-[-24px]" />

              {/* Styling & Customization Section */}
              <Accordion
                defaultExpanded
                sx={{ boxShadow: "none", border: "1px solid #eee" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Styling & Customization
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <HPInput
                        label="Chart title"
                        value={currentConfig.title}
                        onChange={(e) => updateChartTitle(e.target.value)}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: "70%",
                          right: 0,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <ToggleButton
                          value="bold"
                          selected={currentConfig.titleBold}
                          onChange={() =>
                            updateTitleBold(!currentConfig.titleBold)
                          }
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            border: "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                          }}
                        >
                          <FormatBoldIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                          value="underline"
                          selected={currentConfig.titleUnderline}
                          onChange={() =>
                            updateTitleUnderline(!currentConfig.titleUnderline)
                          }
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            border: "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                          }}
                        >
                          <FormatUnderlinedIcon fontSize="small" />
                        </ToggleButton>
                      </Box>
                    </Box>

                    <HPInput
                      label="Subtitle"
                      value={currentConfig.subtitle}
                      onChange={(e) => updateChartSubtitle(e.target.value)}
                      placeholder="Optional"
                    />
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Color Palette
                      </FormLabel>
                      <Select
                        value={currentConfig.colorPalette}
                        onChange={(e) => updateColorPalette(e.target.value)}
                        renderValue={(value) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              justifyContent: "space-between",
                              flexDirection: "row-reverse",
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 12,
                                borderRadius: "16px",
                                background:
                                  {
                                    Multicolor:
                                      "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                                    Gradient:
                                      "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
                                    Grayscale:
                                      "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
                                  }[value as string] || "#57bfe7", // Default to blue
                              }}
                            />
                            {value}
                          </Box>
                        )}
                      >
                        <MenuItem value="Multicolor">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                              }}
                            />
                            Multicolor
                          </Box>
                        </MenuItem>
                        <MenuItem value="Gradient">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
                              }}
                            />
                            Gradient
                          </Box>
                        </MenuItem>
                        <MenuItem value="Grayscale">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
                              }}
                            />
                            Grayscale
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography>Legend</Typography>
                      <IOSSwitch
                        checked={currentConfig.legendEnabled}
                        onChange={(e) => updateLegendEnabled(e.target.checked)}
                        size="small"
                      />
                    </Box>
                    {/* Decimal Places Selection */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-sm">Rounding</FormLabel>
                      <Select
                        value={currentConfig.percentageFormatting}
                        onChange={(e) =>
                          updatePercentageFormatting(Number(e.target.value))
                        }
                      >
                        <MenuItem value={0}>0 decimals</MenuItem>
                        <MenuItem value={1}>1 decimal</MenuItem>
                        <MenuItem value={2}>2 decimals</MenuItem>
                      </Select>
                    </FormControl>
                    {/* Sub-1% Style Radio Buttons - only show when decimals = 0 */}
                    {currentConfig.percentageFormatting === 0 && (
                      <FormControl component="fieldset" fullWidth size="small">
                        <FormLabel className="text-sm">Sub‑1% Style</FormLabel>
                        <RadioGroup
                          value={currentConfig.subOnePercentStyle}
                          onChange={(e) =>
                            updateSubOnePercentStyle(
                              e.target.value as "<1%" | "0%",
                            )
                          }
                          row
                        >
                          <FormControlLabel
                            value="<1%"
                            control={<Radio size="small" />}
                            label="<1%"
                          />
                          <FormControlLabel
                            className="ml-26"
                            value="0%"
                            control={<Radio size="small" />}
                            label="0%"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}

                    {/* Slice Value and Text Placement */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Slice Value and Text placement
                      </FormLabel>
                      <Select
                        value={currentConfig.dataLabelsPosition}
                        onChange={(e) =>
                          updateDataLabelsPosition(
                            e.target.value as "inside" | "outside" | "none",
                          )
                        }
                      >
                        <MenuItem value="inside">Inside</MenuItem>
                        <MenuItem value="outside">Outside</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Slice Value and Text Format */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Slice Value and Text format
                      </FormLabel>
                      <Select
                        value={currentConfig.sliceValueFormat}
                        onChange={(e) =>
                          updateSliceValueFormat(
                            e.target.value as "percentage" | "percentage_text",
                          )
                        }
                      >
                        <MenuItem value="percentage">Percentage only</MenuItem>
                        <MenuItem value="percentage_text">
                          Percentage + Short Response Text
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {/* Minimum Slice Value Text Threshold */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Minimum Slice Value Text Threshold
                      </FormLabel>
                      <Select
                        value={currentConfig.minSliceTextThreshold}
                        onChange={(e) =>
                          updateMinSliceTextThreshold(Number(e.target.value))
                        }
                      >
                        <MenuItem value={1}>1%</MenuItem>
                        <MenuItem value={2}>2%</MenuItem>
                        <MenuItem value={3}>3%</MenuItem>
                        <MenuItem value={4}>4%</MenuItem>
                        <MenuItem value={5}>5%</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Force Show All Values Toggle */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IOSSwitch
                        checked={currentConfig.forceShowAllValues}
                        onChange={(e) =>
                          updateForceShowAllValues(e.target.checked)
                        }
                        size="small"
                      />
                      <Typography>Force Show All Values</Typography>
                    </Box>
                    {/*
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Show data labels</Typography>
                    <IOSSwitch
                      checked={currentConfig.dataLabelsEnabled}
                      onChange={(e) =>
                        updateDataLabelsEnabled(e.target.checked)
                      }
                      size="small"
                    />
                  </Box> */}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Output Values Section */}
              {/* <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Output Values
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Show percentages</Typography>
                    <IOSSwitch checked={true} size="small" disabled />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Show values</Typography>
                    <IOSSwitch
                      checked={currentConfig.showTooltip}
                      onChange={(e) => updateShowTooltip(e.target.checked)}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box> */}
            </Box>
          </Box>

          {/* Preview Panel */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Box
              sx={{
                flex: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                minHeight: 400,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isRefetchingData ? (
                <LoadingSpinner message="Loading data..." />
              ) : isAvailableData ? (
                <EmptyChartState message="This question has no response data available for visualization" />
              ) : (
                <ConfigurableDonutChart />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Source: {currentConfig.series[0]?.name || "Sample Data"}
            </Typography>
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <HPButton variant="text" onClick={handleDiscard}>
            Discard
          </HPButton>
          <Tooltip
            title={isValidationError ? getValidationTooltip() : ""}
            arrow
            placement="top"
          >
            <span>
              <HPButton
                variant="contained"
                onClick={handleSave}
                disabled={
                  isValidationError || isAvailableData || isRefetchingData
                }
              >
                {isCreatingChart ? "Save" : "Update"}
              </HPButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DonutChartConfigDrawer;
